import React from 'react';
import { TableHeader } from '@oysterjs/ui/table';
import { type ColumnDef } from '@oysterjs/ui/data-table';
import { Checkbox } from '@oysterjs/ui/checkbox';
import {
  CaraWorkspaceOverviewFieldsFragment,
  CaraWorkspaceState,
  CaraWorkspaceSortField,
  SortDirection,
  type CaraWorkspaceSortInput
} from '~/api/graphql';
import { getWorkspaceStateTitle } from '~/api/data/workspaces/copilot-filters';
import { Badge } from '@oysterjs/ui/badge';
import { VisibilityBadge } from '~/components/visibility/visibility-badge';

type WS = CaraWorkspaceOverviewFieldsFragment;
export type WorkspaceSortKey =
  | 'title'
  | 'preset'
  | 'status'
  | 'client'
  | 'user'
  | 'started'
  | 'date';
export type WorkspaceGroupKey = 'preset' | 'status' | 'client' | 'user' | 'date';

const formatWorkspaceDate = (value: Date) =>
  new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(new Date(value));

const SORT_KEY_TO_FIELD: Record<WorkspaceSortKey, CaraWorkspaceSortField> = {
  title: CaraWorkspaceSortField.Title,
  preset: CaraWorkspaceSortField.Preset,
  status: CaraWorkspaceSortField.State,
  client: CaraWorkspaceSortField.ClientName,
  user: CaraWorkspaceSortField.AgencyUser,
  started: CaraWorkspaceSortField.CreatedAt,
  date: CaraWorkspaceSortField.UpdatedAt
};

/** Map UI sort key + direction to GraphQL CaraWorkspaceSortInput (default: UPDATED_AT DESC) */
export function workspaceSortToCaraWorkspaceSortInput(
  sortBy: WorkspaceSortKey | undefined,
  sortDirection: 'asc' | 'desc'
): CaraWorkspaceSortInput {
  const field = sortBy ? SORT_KEY_TO_FIELD[sortBy] : CaraWorkspaceSortField.UpdatedAt;
  const direction = sortDirection === 'asc' ? SortDirection.Asc : SortDirection.Desc;
  return { field, direction };
}

const uniq = <T,>(arr: T[]) => [...new Set(arr)];

const titleCase = (s: string) =>
  s
    .split(/[\s_]/g)
    .map((part) => part[0]?.toUpperCase() + part.substring(1).toLowerCase())
    .join(' ');

const getWorkspaceStateColor = (state: CaraWorkspaceState) =>
  ({
    [CaraWorkspaceState.Created]: 'neutral',
    [CaraWorkspaceState.Idle]: 'green',
    [CaraWorkspaceState.WaitingForInput]: 'amber',
    [CaraWorkspaceState.Working]: 'blue'
  })[state] || 'neutral';

export const WorkspaceStateBadge = (props: { state: CaraWorkspaceState }) => (
  <Badge
    // eslint-disable-next-line
    // @ts-ignore
    color={getWorkspaceStateColor(props.state)}
    className="px-1.5 py-0.5 text-[10px]"
  >
    {getWorkspaceStateTitle(props.state)}
  </Badge>
);

const dataColumns: ColumnDef<WS, WorkspaceSortKey, WorkspaceGroupKey>[] = [
  {
    id: 'title',
    header: 'Title / Preset',
    sortKey: 'title',
    groupKey: 'preset',
    renderCell: (workspace) => (
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-sm font-medium text-neutral-900 dark:text-white">
            {workspace.title || (
              <span className="text-xs text-neutral-400 dark:text-neutral-500">—</span>
            )}
            <VisibilityBadge visibility={workspace.visibility} variant="list" />
          </div>
          <div className="text-xs text-neutral-500 dark:text-neutral-400">
            {workspace.preset || workspace.customPreset?.title ? (
              titleCase(workspace.preset || workspace.customPreset?.title || '')
            ) : (
              <span className="text-neutral-400 dark:text-neutral-500">—</span>
            )}
          </div>
        </div>
      </div>
    )
  },
  {
    id: 'status',
    header: 'Status',
    sortKey: 'status',
    groupKey: 'status',
    renderCell: (workspace) => <WorkspaceStateBadge state={workspace.state} />
  },
  {
    id: 'client',
    header: 'Client',
    sortKey: 'client',
    groupKey: 'client',
    renderCell: (workspace) => (
      <div className="truncate max-w-[240px] text-sm text-neutral-900 dark:text-white">
        {workspace.metadata.clientName || (
          <span className="text-xs text-neutral-400 dark:text-neutral-500">—</span>
        )}
      </div>
    )
  },
  {
    id: 'user',
    header: 'User',
    sortKey: 'user',
    groupKey: 'user',
    renderCell: (workspace) => (
      <span className="text-sm text-neutral-600 dark:text-neutral-400">
        {workspace.agencyUser?.name || workspace.integration?.type}
      </span>
    )
  },
  {
    id: 'started',
    header: 'Started At',
    sortKey: 'started',
    renderCell: (workspace) => (
      <span className="text-xs text-neutral-600 dark:text-neutral-400">
        {formatWorkspaceDate(workspace.createdAt)}
      </span>
    )
  },
  {
    id: 'updated',
    header: 'Updated At',
    sortKey: 'date',
    groupKey: 'date',
    renderCell: (workspace) => (
      <span className="text-xs text-neutral-600 dark:text-neutral-400">
        {formatWorkspaceDate(workspace.updatedAt)}
      </span>
    )
  }
];

export const getWorkspaceColumns = ({
  selectedSet,
  setSelectedWorkspaces,
  filtered,
  selectable = true
}: {
  selectedSet: ReadonlySet<string>;
  setSelectedWorkspaces: React.Dispatch<React.SetStateAction<string[]>>;
  filtered: WS[];
  /**
   * Drops the selection column when false. Selecting rows only leads to bulk
   * archive and visibility, both of which are workspace updates.
   */
  selectable?: boolean;
}): ColumnDef<WS, WorkspaceSortKey, WorkspaceGroupKey>[] => [
  ...(selectable
    ? [
        {
          id: 'select',
          noHref: true,
          cellClassName: 'w-0 py-1.5 px-3',
          renderHeader: () => (
            <TableHeader className="w-0 py-1.5 px-3 text-xs">
              <Checkbox
                checked={selectedSet.size > 0 && selectedSet.size === filtered.length}
                color="sky"
                onChange={(v) => {
                  setSelectedWorkspaces((prev) =>
                    v ? uniq([...prev, ...filtered.map((w) => w.id)]) : []
                  );
                }}
              />
            </TableHeader>
          ),
          renderCell: (workspace) => (
            <Checkbox
              checked={selectedSet.has(workspace.id)}
              color="sky"
              onChange={(v) => {
                setSelectedWorkspaces((prev) =>
                  v ? uniq([...prev, workspace.id]) : prev.filter((id) => id !== workspace.id)
                );
              }}
            />
          )
        } satisfies ColumnDef<WS, WorkspaceSortKey, WorkspaceGroupKey>
      ]
    : []),
  ...dataColumns
];
